import React, { useState, useEffect } from 'react';
import LocationFilter from './LocationFilter';

export default function MenuBrowser({ locationId, locationName }) {
  const [locationFilter, setLocationFilter] = useState(locationId || '');
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [checkedGroups, setCheckedGroups] = useState(new Set());
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // Re-seed the dropdown when the global (Sync page) location changes.
  useEffect(() => {
    setLocationFilter(locationId || '');
  }, [locationId]);

  // An empty filter means "All Locations" - still a valid fetch. Checked
  // groups and the last sync result are keyed by menu-group guid, which is
  // meaningless once the visible set of groups changes.
  useEffect(() => {
    setCheckedGroups(new Set());
    setSyncResult(null);
    fetchMenus();
  }, [locationFilter]);

  async function fetchMenus() {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/menus${locationFilter ? `?locationId=${locationFilter}` : ''}`);
      if (!response.ok) throw new Error('Failed to fetch menus');
      const data = await response.json();
      setMenus(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function toggleGroup(guid) {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(guid)) {
      newExpanded.delete(guid);
    } else {
      newExpanded.add(guid);
    }
    setExpandedGroups(newExpanded);
  }

  function toggleChecked(guid) {
    const next = new Set(checkedGroups);
    if (next.has(guid)) {
      next.delete(guid);
    } else {
      next.add(guid);
    }
    setCheckedGroups(next);
    setSyncResult(null);
  }

  function collectAllGroupGuids(groups) {
    const guids = [];
    for (const group of groups) {
      guids.push(group.guid);
      if (group.subgroups && group.subgroups.length > 0) {
        guids.push(...collectAllGroupGuids(group.subgroups));
      }
    }
    return guids;
  }

  const allGroupGuids = menus.flatMap((menu) => collectAllGroupGuids(menu.groups || []));

  function toggleSelectAll() {
    setCheckedGroups((prev) => {
      if (allGroupGuids.length > 0 && allGroupGuids.every((guid) => prev.has(guid))) {
        return new Set();
      }
      return new Set(allGroupGuids);
    });
    setSyncResult(null);
  }

  async function handleSyncSelected() {
    try {
      setSyncing(true);
      setSyncResult(null);
      const response = await fetch('/api/product-class-sync/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ menuGroupGuids: Array.from(checkedGroups) }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Sync failed');
      setSyncResult(data);
      if (!data.failed || data.failed.length === 0) {
        setCheckedGroups(new Set());
      }
    } catch (err) {
      setSyncResult({ written: 0, failed: [], error: err.message });
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div className="menu-browser">
      <h2>📋 Menu Catalog</h2>

      <LocationFilter value={locationFilter} onChange={setLocationFilter} />

      {loading && <p className="loading">Loading menus...</p>}
      {error && <div className="error">✕ {error}</div>}

      {!loading && menus.length === 0 && (
        <p className="empty">No menus found. Try syncing data first.</p>
      )}

      {!loading && menus.length > 0 && (
        <div className="sync-action-bar">
          <label className="select-all-label">
            <input
              type="checkbox"
              checked={allGroupGuids.length > 0 && allGroupGuids.every((guid) => checkedGroups.has(guid))}
              onChange={toggleSelectAll}
            />
            Select All
          </label>
          <button disabled={syncing || checkedGroups.size === 0} onClick={handleSyncSelected}>
            {syncing ? 'Syncing...' : `Sync Selected to ProductClass (${checkedGroups.size})`}
          </button>
        </div>
      )}

      {syncResult && (
        <div className={syncResult.error || syncResult.failed?.length ? 'error' : 'success-message'}>
          {syncResult.error
            ? `Sync failed: ${syncResult.error}`
            : `Wrote ${syncResult.written} document(s), ${syncResult.failed.length} failed`}
          {syncResult.failed && syncResult.failed.length > 0 && (
            <ul>
              {syncResult.failed.map((f) => (
                <li key={f.guid}>{f.guid}: {f.error}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!loading && menus.map(menu => (
        <div key={menu.guid} className="menu-section">
          <h3 className="menu-title">{menu.name}</h3>
          {menu.description && <p className="menu-desc">{menu.description}</p>}

          {menu.groups.length === 0 ? (
            <p className="empty-section">No groups in this menu</p>
          ) : (
            <MenuGroupTree
              groups={menu.groups}
              expandedGroups={expandedGroups}
              toggleGroup={toggleGroup}
              checkedGroups={checkedGroups}
              toggleChecked={toggleChecked}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function MenuGroupTree({ groups, expandedGroups, toggleGroup, checkedGroups, toggleChecked }) {
  return (
    <div className="group-tree">
      {groups.map(group => (
        <MenuGroupNode
          key={group.guid}
          group={group}
          expandedGroups={expandedGroups}
          toggleGroup={toggleGroup}
          checkedGroups={checkedGroups}
          toggleChecked={toggleChecked}
        />
      ))}
    </div>
  );
}

// Groups above this size (e.g. Toast's own catalog items that aren't placed
// on any live menu, dumped into a single "Uncategorized" bucket) render
// collapsed by default so the page doesn't mount thousands of DOM nodes
// up front. Normal-sized groups still show their items immediately.
const LARGE_GROUP_THRESHOLD = 50;

function MenuGroupNode({ group, expandedGroups, toggleGroup, checkedGroups, toggleChecked }) {
  const hasSubgroups = group.subgroups && group.subgroups.length > 0;
  const hasItems = group.items.length > 0;
  const isLargeGroup = group.items.length > LARGE_GROUP_THRESHOLD;
  const itemsNeedToggle = hasItems && isLargeGroup;
  const isExpanded = expandedGroups.has(group.guid);
  const itemsVisible = hasItems && (!isLargeGroup || isExpanded);
  const isExpandable = hasSubgroups || itemsNeedToggle;

  return (
    <div className="group-node">
      <div className="group-header">
        {isExpandable && (
          <button
            className="expand-btn"
            onClick={() => toggleGroup(group.guid)}
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? '▼' : '▶'}
          </button>
        )}
        <input
          type="checkbox"
          className="group-checkbox"
          checked={checkedGroups.has(group.guid)}
          onChange={() => toggleChecked(group.guid)}
          title="Select for ProductClass sync"
        />
        <span className="group-name">{group.name}</span>
        <span className="item-count">{group.items.length} item{group.items.length !== 1 ? 's' : ''}</span>
      </div>

      {itemsVisible && (
        <div className="items-list">
          {group.items.map(item => (
            <div key={item.guid} className="item">
              <div className="item-info">
                <span className="item-name">{item.name}</span>
                {item.description && <span className="item-desc">{item.description}</span>}
                {item.modifierGroups && item.modifierGroups.length > 0 && (
                  <div className="modifier-groups">
                    {item.modifierGroups.map((mg, idx) => (
                      <span key={idx} className="modifier-badge" title={mg.guid}>
                        🔧 Option {idx + 1}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {item.price && <span className="item-price">${item.price.toFixed(2)}</span>}
            </div>
          ))}
        </div>
      )}

      {hasSubgroups && isExpanded && (
        <div className="subgroups">
          <MenuGroupTree
            groups={group.subgroups}
            expandedGroups={expandedGroups}
            toggleGroup={toggleGroup}
            checkedGroups={checkedGroups}
            toggleChecked={toggleChecked}
          />
        </div>
      )}
    </div>
  );
}
